import type { Ref } from 'vue';
import type { OverrideBlockDragConfig, OverrideBlockDragContext, OverrideBlockDragDropContext } from '#layers/block-engine/public';
import type {
	DropTarget,
	GridBlockPosition,
	GridLayoutData,
	PreviewPosition,
} from './grid-types';
import { computed, onUnmounted, ref } from 'vue';
import {
	calculateDropUpdate,
	calculatePreviewPosition,
	findDropTargetInRows,
	safeUpdateGridData,
} from './grid-drag-utils';
import { GRID_CONFIG } from './grid-types';
import { useGridValidation } from './use-grid-validation';

/**
 * State for a block being dragged via the block builder drag system
 */
export interface GridDragState {
	/** The child ID of the block being dragged */
	draggedChildId: string;
	/** Block ID of the block being dragged */
	draggedBlockId: string;
	/** Initial position of the dragged block (if it was already in the grid) */
	initialPosition: GridBlockPosition | null;
	/** Grid positions snapshot captured when the drag first entered the grid */
	blockPositionsBeforeDrag: Record<string, GridBlockPosition>;
	/** Current drop target */
	dropTarget: DropTarget | null;
	/** Between row hover index (for inserting new rows) */
	betweenRowHover: number | null;
}

/**
 * Grid drag composable that integrates with the block builder's drag system.
 *
 * This composable transparently transitions from the standard block builder drag
 * to the grid layout drag system when the mouse enters the grid bounds, and back
 * when it leaves.
 */
export function useGridDragFromBlockBuilder(
	gridData: Ref<GridLayoutData>,
	updateGridData: (data: Partial<GridLayoutData>) => void,
	containerRef: Ref<HTMLElement | null>,
	rowRefs: Ref<Record<number, HTMLElement>>,
	options?: {
		/** Callback to move a block into the grid's slot when dragged from outside */
		moveBlock?: (childId: string) => void;
		/** Callback to duplicate a block into the grid's slot during alt-drag */
		duplicateBlock?: (childId: string) => Promise<string | null>;
	},
) {
	const { validatePosition, tryAccommodate } = useGridValidation(gridData);

	// Drag state (null when not captured)
	const dragging = ref<GridDragState | null>(null);
	const isDragging = computed(() => dragging.value !== null);

	const ctx = {
		gridData,
		validatePosition,
		tryAccommodate: (childId: string, rowIndex: number, colStart: number, colSpan: number) =>
			tryAccommodate(childId, rowIndex, colStart, colSpan, {
				blockPositionsBeforeDrag: dragging.value?.blockPositionsBeforeDrag,
			}),
	};

	// Track whether we're inside bounds (for hysteresis)
	const wasInsideBounds = ref(false);

	// Hysteresis state for row switching
	const lastStableRowIndex = ref<number | null>(null);
	const lastStableMouseY = ref<number | null>(null);

	// Between row hover state.
	// Kept as a separate ref (in addition to dragging.value.betweenRowHover) so it can be
	// exported and used in reactive computed properties without going through dragging.
	const betweenRowHover = ref<number | null>(null);
	const separatorHoverIndex = ref<number | null>(null);
	const pendingBetweenRowHover = ref<number | null>(null);
	let betweenRowHoverTimer: ReturnType<typeof setTimeout> | null = null;

	/**
	 * Check if a point is inside the grid container bounds, with hysteresis
	 */
	function isInsideBounds(
		x: number,
		y: number,
		bounds: { x: number; y: number; width: number; height: number },
	): boolean {
		// Apply hysteresis based on previous state
		const margin = wasInsideBounds.value ? GRID_CONFIG.BOUNDS_HYSTERESIS_PX : 0;

		return (
			x >= bounds.x - margin
			&& x <= bounds.x + bounds.width + margin
			&& y >= bounds.y - margin
			&& y <= bounds.y + bounds.height + margin
		);
	}

	const previewPosition = computed<PreviewPosition | null>(() => {
		if (!dragging.value) return null;

		return calculatePreviewPosition(
			ctx,
			dragging.value.draggedChildId,
			dragging.value.initialPosition,
			dragging.value.dropTarget,
			dragging.value.betweenRowHover,
		);
	});

	const previewGridData = computed<GridLayoutData | null>(() => {
		if (!dragging.value) return null;

		const preview = previewPosition.value;
		if (!preview) return null;

		const { draggedChildId } = dragging.value;

		// Handle inserting before a row
		if (preview.insertBeforeRow !== undefined) {
			const insertIndex = preview.insertBeforeRow;

			// Keep append-row hover from synthesizing a third row while dragging.
			// The separator itself is the affordance for creating the new row.
			if (insertIndex >= gridData.value.rows.length) {
				return {
					rows: [...gridData.value.rows],
					blockPositions: { ...gridData.value.blockPositions },
				};
			}

			const rows = [...gridData.value.rows];
			rows.splice(insertIndex, 0, { columns: GRID_CONFIG.COLUMNS });

			const positions = { ...gridData.value.blockPositions };

			// Shift all blocks in rows >= insertIndex
			for (const [id, pos] of Object.entries(positions)) {
				if (id !== draggedChildId && pos.rowIndex >= insertIndex) {
					positions[id] = { ...pos, rowIndex: pos.rowIndex + 1 };
				}
			}

			positions[draggedChildId] = {
				rowIndex: insertIndex,
				colStart: 0,
				colSpan: GRID_CONFIG.COLUMNS,
			};

			return { rows, blockPositions: positions };
		}

		// Keep the live grid structure stable while previewing a new row at the end.
		// The append-row affordance is rendered separately, so synthesizing a temporary
		// row here only creates duplicate end-of-grid drop targets.
		if (preview.isNewRow && preview.rowIndex >= gridData.value.rows.length) {
			return {
				rows: [...gridData.value.rows],
				blockPositions: { ...gridData.value.blockPositions },
			};
		}

		// Moving within existing rows
		const positions = { ...gridData.value.blockPositions };
		const accommodation = preview.accommodation ?? ctx.tryAccommodate(draggedChildId, preview.rowIndex, preview.colStart, preview.colSpan);

		if (accommodation.canAccommodate && accommodation.updatedPositions) {
			Object.assign(positions, accommodation.updatedPositions);
		}
		else {
			positions[draggedChildId] = {
				rowIndex: preview.rowIndex,
				colStart: preview.colStart,
				colSpan: preview.colSpan,
			};
		}

		return { rows: [...gridData.value.rows], blockPositions: positions };
	});

	/** Resolves the grid target using the canvas grab point when drag-start geometry exists. */
	function findDropTarget({ mouse, draggedBlock }: OverrideBlockDragContext): DropTarget | null {
		if (!dragging.value || !containerRef.value) return null;

		const gridRows = Object.values(rowRefs.value);

		const result = findDropTargetInRows(
			ctx,
			dragging.value.draggedChildId,
			dragging.value.initialPosition,
			mouse.x,
			mouse.y,
			gridRows,
			lastStableRowIndex.value,
			lastStableMouseY.value,
			draggedBlock.source === 'canvas' && draggedBlock.bounds && draggedBlock.pointerOffset
				? {
						initialMouseX: draggedBlock.bounds.x + draggedBlock.pointerOffset.x,
						pointerOffsetX: draggedBlock.pointerOffset.x,
					}
				: undefined,
		);

		if (!result) return null;

		lastStableRowIndex.value = result.newStableRowIndex;
		lastStableMouseY.value = result.newStableMouseY;

		return result.target;
	}

	function applyBetweenRowHover(rowIndex: number | null) {
		betweenRowHover.value = rowIndex;

		if (dragging.value) {
			dragging.value.betweenRowHover = rowIndex;

			if (rowIndex !== null) {
				dragging.value.dropTarget = null;
				lastStableRowIndex.value = null;
				lastStableMouseY.value = null;
			}
		}
	}

	function clearBetweenRowHoverTimer() {
		if (betweenRowHoverTimer !== null) {
			clearTimeout(betweenRowHoverTimer);
			betweenRowHoverTimer = null;
		}

		pendingBetweenRowHover.value = null;
	}

	function scheduleBetweenRowHover(rowIndex: number) {
		separatorHoverIndex.value = rowIndex;

		if (!dragging.value) {
			return;
		}

		if (betweenRowHover.value === rowIndex) {
			clearBetweenRowHoverTimer();
			return;
		}

		if (pendingBetweenRowHover.value === rowIndex) {
			return;
		}

		clearBetweenRowHoverTimer();
		pendingBetweenRowHover.value = rowIndex;

		betweenRowHoverTimer = setTimeout(() => {
			betweenRowHoverTimer = null;
			pendingBetweenRowHover.value = null;

			if (separatorHoverIndex.value === rowIndex && dragging.value) {
				applyBetweenRowHover(rowIndex);
			}
		}, GRID_CONFIG.BETWEEN_ROW_ENTER_DELAY_MS);
	}

	function clearBetweenRowHover(options: { immediate?: boolean } = {}) {
		separatorHoverIndex.value = null;
		clearBetweenRowHoverTimer();

		if (options.immediate || !dragging.value || betweenRowHover.value === null) {
			applyBetweenRowHover(null);
		}
	}

	function cleanup() {
		clearBetweenRowHoverTimer();
		dragging.value = null;
		betweenRowHover.value = null;
		separatorHoverIndex.value = null;
		wasInsideBounds.value = false;
		lastStableRowIndex.value = null;
		lastStableMouseY.value = null;
	}

	// Create the override config that integrates with block builder's drag system
	const overrideConfig: OverrideBlockDragConfig = {
		onDragOver(context: OverrideBlockDragContext) {
			const { draggedBlock, mouse, bounds } = context;

			if (!draggedBlock.childId) {
				return { captured: false };
			}

			// Check if mouse is inside bounds with hysteresis
			const inside = isInsideBounds(mouse.x, mouse.y, bounds);

			if (!inside) {
				// We're outside the bounds - release capture
				if (wasInsideBounds.value) {
					// Just left the bounds
					wasInsideBounds.value = false;
				}

				return { captured: false };
			}

			// We're inside the bounds
			wasInsideBounds.value = true;

			// Initialize or update drag state
			if (!dragging.value || dragging.value.draggedChildId !== draggedBlock.childId) {
				// Get the initial position if the block is already in this grid
				const initialPosition = gridData.value.blockPositions[draggedBlock.childId] ?? null;

				dragging.value = {
					draggedChildId: draggedBlock.childId,
					draggedBlockId: draggedBlock.blockId,
					initialPosition,
					blockPositionsBeforeDrag: { ...gridData.value.blockPositions },
					dropTarget: null,
					betweenRowHover: null,
				};

				// Reset hysteresis for new drag
				clearBetweenRowHover({ immediate: true });
				lastStableRowIndex.value = null;
				lastStableMouseY.value = null;
			}

			// Calculate drop target
			const target = findDropTarget(context);

			if (
				separatorHoverIndex.value === null
				&& pendingBetweenRowHover.value === null
				&& betweenRowHover.value !== null
				&& target
			) {
				applyBetweenRowHover(null);
			}

			if (dragging.value && betweenRowHover.value === null) {
				dragging.value.dropTarget = target;
			}

			return { captured: true };
		},

		onDrop(context: OverrideBlockDragDropContext) {
			void handleDrop(context);
		},

		onDragLeave(_context: OverrideBlockDragContext) {
			cleanup();
		},

		onDragCancel: cleanup,
	};

	async function handleDrop(context: OverrideBlockDragDropContext) {
		const dragState = dragging.value;

		if (!dragState || !dragState.draggedChildId) {
			cleanup();
			return;
		}

		// Only proceed if the drop can actually be applied. The valid flag already accounts
		// for accommodation (see findDropTargetInRows). Skipping here prevents side effects
		// (moveBlock / duplicateBlock) when there is nowhere to place the block.
		const canApplyDrop = dragState.dropTarget?.valid === true || dragState.betweenRowHover !== null;

		if (!canApplyDrop) {
			cleanup();
			return;
		}

		try {
			let committedChildId = dragState.draggedChildId;
			let committedInitialPosition = dragState.initialPosition;

			if (context.altKey) {
				committedChildId = await options?.duplicateBlock?.(dragState.draggedChildId) ?? '';
				committedInitialPosition = null;
			}
			else if (dragState.initialPosition === null && options?.moveBlock) {
				options.moveBlock(dragState.draggedChildId);
			}

			if (!committedChildId) {
				return;
			}

			const updates = calculateDropUpdate(
				ctx,
				committedChildId,
				committedInitialPosition,
				dragState.dropTarget,
				dragState.betweenRowHover,
			);

			if (updates) {
				safeUpdateGridData(gridData.value, updates, updateGridData, '[GridDragFromBlockBuilder]');
			}
		}
		finally {
			cleanup();
		}
	}

	onUnmounted(() => {
		cleanup();
	});

	return {
		// State
		dragging,
		isDragging,
		previewPosition,
		previewGridData,
		betweenRowHover,

		// The override config to register with the block builder
		overrideConfig,

		// Methods
		scheduleBetweenRowHover,
		clearBetweenRowHover,
		cleanup,
	};
}
